import { APP_ROUTES } from '@/common/constants'

/**
 * Defines which devices a step applies to
 */
type DeviceTarget = 'all' | 'mobile' | 'desktop'

/**
 * Represents a single step in the onboarding flow
 */
interface OnboardingStep {
    route: string
    order: number
    devices: DeviceTarget
    label?: string
    /** Number of sub-screens within this step (e.g., KYC has 4 FrankieOne screens) */
    subSteps?: number
}

/**
 * Complete onboarding flow definition
 * This is the SINGLE SOURCE OF TRUTH for the entire onboarding sequence
 *
 * NOTE: KYC step has 5 internal sub-screens from FrankieOne SDK (Consent, Personal, Document, Review, Result)
 */
const ONBOARDING_FLOW: readonly OnboardingStep[] = [
    { route: APP_ROUTES.CONFIRM_USER, order: 1, devices: 'all', label: 'Confirm Email' },
    { route: APP_ROUTES.ABOUT_YOU, order: 2, devices: 'desktop', label: 'About You' },
    { route: APP_ROUTES.NAME, order: 2, devices: 'mobile', label: 'Name' },
    { route: APP_ROUTES.ADDRESS, order: 3, devices: 'mobile', label: 'Address' },
    { route: APP_ROUTES.MOBILE_NUMBER, order: 4, devices: 'mobile', label: 'Mobile Number' },
    { route: APP_ROUTES.MOBILE_VERIFICATION, order: 5, devices: 'all', label: 'Verify Mobile' },
    { route: APP_ROUTES.SURVEY, order: 6, devices: 'all', label: 'Survey' },
    { route: APP_ROUTES.ACCOUNT_TYPE, order: 7, devices: 'all', label: 'Account Type' },
    { route: APP_ROUTES.TERMS_AND_CONDITIONS, order: 8, devices: 'all', label: 'Terms & Conditions' },
    { route: APP_ROUTES.KYC_INIT, order: 9, devices: 'all', label: 'KYC Setup' },
    { route: APP_ROUTES.KYC, order: 10, devices: 'all', label: 'KYC Verification', subSteps: 5 },
    { route: APP_ROUTES.BANK_ACCOUNT, order: 11, devices: 'all', label: 'Bank Account' },
] as const

/**
 * Filter steps based on device type
 */
function getApplicableSteps(isMobile: boolean): readonly OnboardingStep[] {
    return ONBOARDING_FLOW.filter(
        (step) =>
            step.devices === 'all' ||
            (step.devices === 'mobile' && isMobile) ||
            (step.devices === 'desktop' && !isMobile),
    ).sort((a, b) => a.order - b.order)
}

/**
 * Get progress percentage for current route
 * @param currentRoute - The current route path
 * @param isMobile - Whether the user is on mobile
 * @returns Progress percentage (0-100)
 */
export function getOnboardingProgress(currentRoute: string, isMobile: boolean): number {
    const applicableSteps = getApplicableSteps(isMobile)
    const currentStepIndex = applicableSteps.findIndex((step) => step.route === currentRoute)

    if (currentStepIndex === -1) {
        return 0
    }

    return ((currentStepIndex + 1) / applicableSteps.length) * 100
}

/**
 * Get ordered list of route paths for the onboarding flow
 * @param isMobile - Whether the user is on mobile
 * @returns Array of route paths in order
 */
export function getOnboardingRoutes(isMobile: boolean): readonly string[] {
    return getApplicableSteps(isMobile).map((step) => step.route)
}

/**
 * Get the next step in the onboarding flow
 * @param currentRoute - The current route path
 * @param isMobile - Whether the user is on mobile
 * @returns Next route path or null if at the end
 */
export function getNextOnboardingStep(currentRoute: string, isMobile: boolean): string | null {
    const applicableSteps = getApplicableSteps(isMobile)
    const currentStepIndex = applicableSteps.findIndex((step) => step.route === currentRoute)

    if (currentStepIndex === -1 || currentStepIndex === applicableSteps.length - 1) {
        return null
    }

    return applicableSteps[currentStepIndex + 1].route
}

/**
 * Get the previous step in the onboarding flow
 * @param currentRoute - The current route path
 * @param isMobile - Whether the user is on mobile
 * @returns Previous route path or null if at the beginning
 */
export function getPreviousOnboardingStep(currentRoute: string, isMobile: boolean): string | null {
    const applicableSteps = getApplicableSteps(isMobile)
    const currentStepIndex = applicableSteps.findIndex((step) => step.route === currentRoute)

    if (currentStepIndex <= 0) {
        return null
    }

    return applicableSteps[currentStepIndex - 1].route
}

/**
 * Get total number of steps in the onboarding flow
 * @param isMobile - Whether the user is on mobile
 * @returns Total step count
 */
export function getTotalOnboardingSteps(isMobile: boolean): number {
    return getApplicableSteps(isMobile).length
}

/**
 * Get current step number (1-indexed)
 * @param currentRoute - The current route path
 * @param isMobile - Whether the user is on mobile
 * @returns Current step number or 0 if route not found
 */
export function getCurrentStepNumber(currentRoute: string, isMobile: boolean): number {
    const applicableSteps = getApplicableSteps(isMobile)
    const currentStepIndex = applicableSteps.findIndex((step) => step.route === currentRoute)

    return currentStepIndex === -1 ? 0 : currentStepIndex + 1
}

/**
 * Check if a route is part of the onboarding flow
 * @param route - The route path to check
 * @param isMobile - Whether the user is on mobile
 * @returns True if route is in the onboarding flow
 */
export function isOnboardingRoute(route: string, isMobile: boolean): boolean {
    return getApplicableSteps(isMobile).some((step) => step.route === route)
}

/**
 * Get the total number of steps including sub-steps
 * Used for accurate progress calculation when steps have internal screens
 *
 * @param isMobile - Whether the user is on mobile
 * @returns Total step count including sub-steps
 */
export function getTotalStepsIncludingSubSteps(isMobile: boolean): number {
    const applicableSteps = getApplicableSteps(isMobile)
    return applicableSteps.reduce((total, step) => {
        return total + (step.subSteps || 1)
    }, 0)
}

/**
 * Calculate progress with sub-step support
 * Handles routes that have multiple internal screens (e.g., KYC has 4 FrankieOne screens)
 *
 * @param currentRoute - The current route path
 * @param isMobile - Whether the user is on mobile
 * @param subProgress - Progress within the current step (0-100), used for multi-screen steps
 * @returns Progress percentage (0-100)
 *
 * @example
 * // On KYC step, 50% through the 4 internal screens
 * getProgressWithSubSteps(APP_ROUTES.KYC, false, 50)
 */
export function getProgressWithSubSteps(currentRoute: string, isMobile: boolean, subProgress = 0): number {
    const applicableSteps = getApplicableSteps(isMobile)
    const currentStepIndex = applicableSteps.findIndex((step) => step.route === currentRoute)

    if (currentStepIndex === -1) {
        return 0
    }

    const totalSteps = getTotalStepsIncludingSubSteps(isMobile)

    // Count steps completed before current step (accounting for sub-steps)
    let stepsCompleted = 0
    for (let i = 0; i < currentStepIndex; i++) {
        stepsCompleted += applicableSteps[i].subSteps || 1
    }

    // Add progress within current step
    const currentStep = applicableSteps[currentStepIndex]
    const currentStepSubSteps = currentStep.subSteps || 1
    const currentStepProgress = (subProgress / 100) * currentStepSubSteps

    const totalProgress = stepsCompleted + currentStepProgress

    return (totalProgress / totalSteps) * 100
}

/**
 * Get step information for a route
 *
 * @param route - The route path
 * @param isMobile - Whether the user is on mobile
 * @returns Step information or null if not found
 */
export function getStepInfo(route: string, isMobile: boolean): OnboardingStep | null {
    const applicableSteps = getApplicableSteps(isMobile)
    return applicableSteps.find((step) => step.route === route) || null
}
